import { Fragment, useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Ingredient {
  id: string;
  name: string;
  unit: string;
  quantity: string;
  lowThreshold: string;
  unitCost?: string | null;
  value?: string | null;
  low: boolean;
  lastCountedAt?: string | null;
}

interface Summary {
  tracked: number;
  low: number;
  lowNames: string[];
  value: string;
  valuedItems: number;
}

const REASONS: Array<[string, string, string]> = [
  ['delivery', 'Delivery in', 'Stock arriving from a supplier'],
  ['count', 'Counted', 'What you physically weighed — sets the figure'],
  ['used', 'Used', 'Taken for baking'],
  ['waste', 'Waste', 'Spoiled, spilled or thrown out'],
];

/**
 * Ingredient stock. Separate from products because it is weighed rather than
 * counted, and because negative flour is meaningless where negative bread is
 * a prompt to count.
 */
export default function IngredientsPanel({
  branchId,
  branchName,
  isManager,
}: {
  branchId: string;
  branchName?: string;
  isManager: boolean;
}) {
  const [items, setItems] = useState<Ingredient[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [movements, setMovements] = useState<any[]>([]);
  const [showMovements, setShowMovements] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // moving stock
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [reason, setReason] = useState('delivery');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  // adding an ingredient
  const [showAdd, setShowAdd] = useState(false);
  const [nName, setNName] = useState('');
  const [nUnit, setNUnit] = useState('kg');
  const [nLow, setNLow] = useState('');
  const [nCost, setNCost] = useState('');

  const flash = (m: string) => {
    setMsg(m);
    setErr('');
    setTimeout(() => setMsg(''), 4000);
  };
  const fail = (e: any, fallback: string) => {
    const d = e?.response?.data?.message;
    setErr(Array.isArray(d) ? d.join(', ') : d || fallback);
    setMsg('');
  };

  const load = async () => {
    if (!branchId) return;
    try {
      const [l, s, m] = await Promise.all([
        apiClient.listIngredients(branchId),
        apiClient.ingredientSummary(branchId),
        apiClient.ingredientMovements(branchId),
      ]);
      setItems(Array.isArray(l.data) ? l.data : []);
      setSummary(s.data);
      setMovements(Array.isArray(m.data) ? m.data : []);
    } catch (e: any) {
      fail(e, 'Could not load ingredients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const move = async (ing: Ingredient) => {
    if (!amount.trim()) {
      setErr('How much?');
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.moveIngredient(branchId, ing.id, reason, amount, note.trim() || undefined);
      flash(res.data.message);
      setOpenFor(null);
      setAmount('');
      setNote('');
      await load();
    } catch (e: any) {
      fail(e, 'Could not record that');
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (!nName.trim()) {
      setErr('The ingredient needs a name.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.createIngredient(nName.trim(), nUnit.trim() || 'kg', nLow.trim() || undefined, nCost.trim() || undefined);
      flash(res.data.message);
      setNName('');
      setNLow('');
      setNCost('');
      setShowAdd(false);
      await load();
    } catch (e: any) {
      fail(e, 'Could not add that');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading…</div>;

  const field =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent';

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className={`rounded-lg shadow p-4 ${summary.low > 0 ? 'bg-red-50 border border-red-200' : 'bg-white'}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Running low</p>
            <p className={`text-2xl font-bold tabular-nums ${summary.low > 0 ? 'text-red-700' : 'text-gray-900'}`}>
              {summary.low}
            </p>
            {summary.low > 0 && (
              <p className="text-xs text-red-700 mt-0.5 truncate" title={summary.lowNames.join(', ')}>
                {summary.lowNames.join(', ')}
              </p>
            )}
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Ingredients tracked</p>
            <p className="text-2xl font-bold tabular-nums text-gray-900">{summary.tracked}</p>
            <p className="text-xs text-gray-500 mt-0.5">{branchName}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Store value</p>
            <p className="text-2xl font-bold tabular-nums text-gray-900">R {summary.value}</p>
            {summary.valuedItems < summary.tracked && (
              <p className="text-xs text-gray-500 mt-0.5">
                {summary.tracked - summary.valuedItems} without a cost, so this is partial
              </p>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between gap-4 flex-wrap">
          <h3 className="text-lg font-medium text-gray-900">What is in the store</h3>
          <div className="flex gap-4 items-center shrink-0">
            <button
              onClick={() => setShowMovements(!showMovements)}
              className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
            >
              {showMovements ? 'Hide movements' : 'Show movements'}
            </button>
            {isManager && (
              <button
                onClick={() => setShowAdd(!showAdd)}
                className="text-sm text-amber-600 hover:underline font-medium"
              >
                + Add an ingredient
              </button>
            )}
          </div>
        </div>

        {showAdd && isManager && (
          <div className="px-6 py-4 border-b bg-amber-50 grid grid-cols-1 sm:grid-cols-5 gap-3">
            <input value={nName} onChange={(e) => setNName(e.target.value)} placeholder="Name, e.g. Cake Flour" className={`${field} sm:col-span-2`} />
            <input value={nUnit} onChange={(e) => setNUnit(e.target.value)} placeholder="Unit, e.g. kg" className={field} />
            <input value={nLow} onChange={(e) => setNLow(e.target.value)} placeholder="Warn below" inputMode="decimal" className={`${field} tabular-nums`} />
            <div className="flex gap-2">
              <input value={nCost} onChange={(e) => setNCost(e.target.value)} placeholder="Cost/unit" inputMode="decimal" className={`${field} tabular-nums`} />
              <button onClick={add} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-4 rounded-lg shrink-0">
                Add
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td className="py-12 text-center text-gray-500">
                    No ingredients yet. {isManager ? 'Add the ones you buy in.' : 'A manager sets these up.'}
                  </td>
                </tr>
              ) : (
                items.map((i) => (
                  <Fragment key={i.id}>
                    <tr className={`border-b ${i.low ? 'bg-red-50' : ''}`}>
                      <td className="py-3 px-6">
                        <p className="font-medium text-gray-900">{i.name}</p>
                        <p className="text-xs text-gray-500">
                          {i.unitCost ? `R ${i.unitCost}/${i.unit}` : 'no cost set'}
                          {Number(i.lowThreshold) > 0 ? ` · warn below ${i.lowThreshold}` : ''}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <p className={`tabular-nums font-bold text-lg ${i.low ? 'text-red-700' : 'text-gray-900'}`}>
                          {Number(i.quantity).toFixed(2)}
                          <span className="text-xs font-normal text-gray-500 ml-1">{i.unit}</span>
                        </p>
                        {i.value && <p className="text-xs text-gray-500 tabular-nums">R {i.value}</p>}
                      </td>
                      <td className="py-3 px-6 text-right w-32">
                        <button
                          onClick={() => {
                            setOpenFor(openFor === i.id ? null : i.id);
                            setAmount('');
                            setNote('');
                          }}
                          className="text-sm bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded font-medium"
                        >
                          {openFor === i.id ? 'Close' : 'Move stock'}
                        </button>
                      </td>
                    </tr>
                    {openFor === i.id && (
                      <tr className="border-b bg-gray-50">
                        <td colSpan={3} className="px-6 py-4">
                          <div className="flex gap-3 flex-wrap items-end">
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                What happened
                              </label>
                              <select value={reason} onChange={(e) => setReason(e.target.value)} className={field}>
                                {REASONS.map(([id, label]) => (
                                  <option key={id} value={id}>
                                    {label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                {reason === 'count' ? `Counted (${i.unit})` : `How much (${i.unit})`}
                              </label>
                              <input
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && move(i)}
                                inputMode="decimal"
                                autoFocus
                                className={`${field} tabular-nums w-32`}
                              />
                            </div>
                            <div className="flex-1 min-w-40">
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                Note
                              </label>
                              <input value={note} onChange={(e) => setNote(e.target.value)} className={field} />
                            </div>
                            <button
                              onClick={() => move(i)}
                              disabled={busy}
                              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-lg"
                            >
                              Record
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {REASONS.find(([id]) => id === reason)?.[2]}
                          </p>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showMovements && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">Recent movements</h3>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {movements.length === 0 ? (
                  <tr>
                    <td className="py-10 text-center text-gray-500">Nothing recorded yet.</td>
                  </tr>
                ) : (
                  movements.map((m) => (
                    <tr key={m.id} className="border-b last:border-0">
                      <td className="py-2.5 px-6">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700 capitalize">
                          {m.reason}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-900">{m.ingredientName}</td>
                      <td
                        className={`py-2.5 px-3 text-right tabular-nums font-semibold ${
                          Number(m.change) > 0 ? 'text-green-700' : 'text-red-700'
                        }`}
                      >
                        {Number(m.change) > 0 ? '+' : ''}
                        {Number(m.change).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-gray-500">
                        → {Number(m.balanceAfter).toFixed(2)} {m.unit}
                      </td>
                      <td className="py-2.5 px-6 text-right text-xs text-gray-500">
                        {new Date(m.createdAt).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                        <span className="block">{m.staffName}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
